import { faHandshake, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, IconButton, Typography } from '@mui/material'
import { Popover, Stack, Tooltip } from '@vapor/material'
import { useCallback, useState } from 'react'
import CompanyLookup from '../../customer-lookup/company-lookup'
import { RateFragment, SimpleCustomerLookupFragment, useRatesQuery } from 'src/api/api.generated'
import RateSingleSelect from '../rate-single-select'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { FormItem } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

type FindCompanyConventionsProps = {
  propertyId: string
  title: string
  closeOnChange?: true
  disabled?: boolean
  onConventionSelected?: (rate: RateFragment | undefined, companyId: string | undefined) => void
  clearConventionSelected: () => void
}

const SELECTED_RATE_ID = 'affiliated-rate-id'

export default function FindCompanyConventions(props: FindCompanyConventionsProps) {
  const { title, propertyId, closeOnChange, onConventionSelected, clearConventionSelected, disabled } = props
  const localizeString = useLocalizeString()
  const showError = useErrorSnackBar()
  const { t } = useTranslation()
  const { id: tenantId } = useTenant()
  const [selectedCompany, setSelectedCompany] = useState<SimpleCustomerLookupFragment | null>(null)
  const [selectedRateFragment, setSelectedRateFragment] = useState<RateFragment | undefined>()

  const { sm } = useBreakpoints()

  const { data, loading } = useRatesQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: propertyId,
        },
      },
    },
    onError: showError,
  })

  const rateWithConventions = data?.rates?.filter((r) => (selectedCompany ? (r.affiliatedCompaniesId ?? []).includes(selectedCompany.id) : true)).map((r) => r.id) ?? []
  const conventionedCompanies = data?.rates?.map((r) => r.affiliatedCompaniesId).reduce((prev, curr) => [...(prev ?? []), ...(curr ?? [])], []) ?? []

  //#region popover
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  //#endregion

  const clearSelection = useCallback(() => {
    clearConventionSelected()
    handleClose()
    setSelectedCompany(null)
    setSelectedRateFragment(undefined)
  }, [clearConventionSelected, handleClose, setSelectedCompany, setSelectedRateFragment])

  function onRateChange(value: RateFragment | undefined, company: SimpleCustomerLookupFragment | null) {
    setSelectedRateFragment(value)

    if (onConventionSelected && value && company) {
      onConventionSelected(value, company.id)
      sessionStorage.setItem(SELECTED_RATE_ID, value.id)
      if (closeOnChange) {
        handleClose()
      }
    }
  }

  const label = `${selectedRateFragment ? localizeString(selectedRateFragment?.name) : ''}${selectedCompany ? ` (${selectedCompany.name})` : ''}`

  return (
    <>
      {!selectedRateFragment && (
        <Button size="small" variant="text" disabled={disabled} startIcon={<FontAwesomeIcon icon={faHandshake} />} onClick={handleClick}>
          {title}
        </Button>
      )}
      {Boolean(selectedRateFragment) && (
        <table className="table" style={{ maxWidth: '260px', border: '1px solid rgb(224,224,224)' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid rgb(224,224,224)' }}>
                <Stack direction="row" spacing={0.5} justifyContent="flex-start" alignItems="center" ml={1}>
                  <FontAwesomeIcon icon={faHandshake} color="var(--DarkImperialBlue)" />
                  <Typography variant="body">
                    <b>{t('pms.booking.reservation_new.applied_company_convention')}</b>
                  </Typography>
                </Stack>
              </th>
              <th style={{ width: '70px', border: '1px solid rgb(224,224,224)' }}>
                <Typography variant="body" style={{ maxWidth: '40px !important' }}>
                  <b>{t('commons.table_actions')}</b>
                </Typography>
              </th>
            </tr>
            <tr>
              <td style={{ border: '1px solid rgb(224,224,224)' }}>
                <Stack direction="row" spacing={0} justifyContent="flex-start" alignItems="center" ml={1}>
                  <Tooltip title={label}>
                    <Typography noWrap style={{ width: 'calc(100%)' }} variant="body">
                      {label}
                    </Typography>
                  </Tooltip>
                </Stack>
              </td>

              <td style={{ border: '1px solid rgb(224,224,224)' }}>
                <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                  <IconButton size="small" onClick={handleClick}>
                    <FontAwesomeIcon icon={faPencil} color="var(--RichElectricBlue)" />
                  </IconButton>
                  <IconButton size="small" onClick={clearSelection}>
                    <FontAwesomeIcon icon={faTrash} color="var(--MaximumRed)" />
                  </IconButton>
                </Stack>
              </td>
            </tr>
          </thead>
        </table>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack direction="column" m={1}>
          <Stack direction="row">
            <Stack direction="row" spacing={0.5} justifyContent="flex-start" alignItems="center">
              <FontAwesomeIcon icon={faHandshake} />
              <Typography variant="body">
                <b>{title}</b>
              </Typography>
            </Stack>
            <div style={{ flexGrow: 2 }} />
            <IconButton size="small" onClick={handleClose}>
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          </Stack>

          <Stack direction={sm ? 'row' : 'column'} spacing={1} minWidth="340px" justifyContent="center" alignItems="center">
            <FormItem fullWidth fieldLabel={t('pms.booking.reservation_new.company_convention.customer')}>
              <CompanyLookup
                disabled={loading}
                onChange={(newCompany) => {
                  setSelectedCompany(newCompany)

                  if (newCompany === null) return

                  const ratesWithNewCompany = data?.rates?.filter((r) => r.affiliatedCompaniesId?.includes(newCompany.id))
                  if (ratesWithNewCompany && ratesWithNewCompany.length === 1) {
                    onRateChange(ratesWithNewCompany[0], newCompany)
                  }
                }}
                value={selectedCompany}
                selectableCustomers={conventionedCompanies}
                placeholder={undefined}
              />
            </FormItem>
            <FormItem fullWidth fieldLabel={t('pms.booking.reservation_new.company_convention.rate')}>
              <RateSingleSelect
                propertyId={propertyId}
                value={selectedRateFragment?.id ?? undefined}
                disabled={selectedCompany === null || loading}
                controlledOptions={rateWithConventions}
                onChange={(rateId) => {
                  const rate = data?.rates?.find((r) => r.id === rateId)
                  onRateChange(rate, selectedCompany)
                }}
              />
            </FormItem>
            {selectedRateFragment && (
              <FormItem forceLabelMargin style={{ width: '60px' }}>
                <IconButton size="small" onClick={clearSelection}>
                  <FontAwesomeIcon icon={faTrash} color="var(--MaximumRed)" />
                </IconButton>
              </FormItem>
            )}
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
